import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
// For now we're not using Apollo mutations
// import { useMutation, useQuery } from "@apollo/client";
// import { UPDATE_ROUTING_FORM } from "./graphql/operations";
import {
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";

const Card = styled(MuiCard)(spacing);

const MemberItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

function FormTab({ form, setForm }) {
  // Initialize with empty values - will be populated from form prop
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    sendEmailTo: [],
    addAllTeamMembers: false,
  });

  const [availableMembers, setAvailableMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for tracking loading status
  const [updateLoading, setUpdateLoading] = useState(false);

  // Load team members - could be replaced with a GraphQL query in the future
  useEffect(() => {
    // In a real app, this would be fetched from your API
    const mockTeamMembers = [];

    setAvailableMembers(mockTeamMembers);
  }, []);

  // Update local form data when the form prop changes
  useEffect(() => {
    if (form) {
      console.log("FormTab: form prop changed, updating local state:", form);
      console.log("FormTab: form.name =", form.name);
      console.log("FormTab: form.description =", form.description);
      console.log("FormTab: form.emailSettings =", form.emailSettings);

      setFormData({
        title: form.name || "",
        description: form.description || "",
        sendEmailTo: form.emailSettings?.recipients || [],
        addAllTeamMembers: form.emailSettings?.addAllMembers || false,
      });
    }
  }, [form]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle switch toggle
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  // Handle member selection
  const handleMemberChange = (e) => {
    setSelectedMember(e.target.value);
  };

  // Add a member to recipients
  const handleAddMember = () => {
    if (!selectedMember) return;

    const member = availableMembers.find((m) => m.id === selectedMember);
    if (!member) return;

    // Check if member is already in the list
    if (formData.sendEmailTo.some((m) => m.id === member.id)) return;

    setFormData({
      ...formData,
      sendEmailTo: [...formData.sendEmailTo, member],
    });

    setSelectedMember("");
  };

  // Remove a member from recipients
  const handleRemoveMember = (memberId) => {
    setFormData({
      ...formData,
      sendEmailTo: formData.sendEmailTo.filter((m) => m.id !== memberId),
    });
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  // Handle save
  const handleSave = async () => {
    try {
      // Validate form ID
      const formId = form?._id || form?.id;

      if (!formId) {
        throw new Error("Form ID is missing");
      }

      // Show loading state
      setUpdateLoading(true);

      // Create email settings object
      const emailSettings = {
        recipients: formData.sendEmailTo,
        addAllMembers: formData.addAllTeamMembers,
      };

      // Import axiosInstance for API call
      const { axiosInstance } = await import("../../../utils/axios");

      // Call the backend to update the form
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation UpdateRoutingForm($id: ID!, $input: RoutingFormInput!) {
            updateRoutingForm(id: $id, input: $input) {
              _id
              name
              description
              status
              fields
              routes
              fallback
              emailSettings
              createdAt
              updatedAt
            }
          }
        `,
        variables: {
          id: formId,
          input: {
            name: formData.title,
            description: formData.description,
            emailSettings: emailSettings,
          },
        },
      });

      console.log("Update response:", response.data);

      const result =
        response.data?.data?.updateRoutingForm ||
        response.data?.updateRoutingForm;

      if (result) {
        // Update the parent component's state with the server response
        setForm({
          ...form,
          ...result,
          // Parse JSON fields if they are strings
          fields:
            typeof result.fields === "string"
              ? JSON.parse(result.fields)
              : result.fields || form.fields,
          routes:
            typeof result.routes === "string"
              ? JSON.parse(result.routes)
              : result.routes || form.routes,
          fallback:
            typeof result.fallback === "string"
              ? JSON.parse(result.fallback)
              : result.fallback || form.fallback,
          emailSettings:
            typeof result.emailSettings === "string"
              ? JSON.parse(result.emailSettings)
              : result.emailSettings || emailSettings,
        });

        setNotification({
          open: true,
          message: "Form settings saved successfully",
          severity: "success",
        });
      } else {
        throw new Error("No data returned from server");
      }
    } catch (error) {
      console.error("Error updating form settings:", error);
      setNotification({
        open: true,
        message: `Error updating form settings: ${error.message}`,
        severity: "error",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Form Settings
      </Typography>
      <Typography variant="body2" mb={3}>
        Configure your form's basic information and notification settings
      </Typography>

      <Card mb={4}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id="title"
                name="title"
                label="Title"
                variant="outlined"
                fullWidth
                value={formData.title}
                onChange={handleChange}
                helperText="The name of your routing form"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange}
                helperText="A brief description of what this form is used for"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card mb={4}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Email Notifications
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Configure who receives email notifications when this form is
            submitted
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  name="addAllTeamMembers"
                  checked={formData.addAllTeamMembers}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label="Notify all team members (including future members)"
            />
          </FormGroup>

          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              Add Specific Team Members
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth disabled={formData.addAllTeamMembers}>
                  <Select
                    displayEmpty
                    value={selectedMember}
                    onChange={handleMemberChange}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <em>Select team members to notify</em>;
                      }

                      const member = availableMembers.find(
                        (m) => m.id === selected
                      );
                      return member ? member.name : "";
                    }}
                  >
                    <MenuItem disabled value="">
                      <em>Select team members to notify</em>
                    </MenuItem>
                    {availableMembers
                      .filter(
                        (m) =>
                          !formData.sendEmailTo.some((sm) => sm.id === m.id)
                      )
                      .map((member) => (
                        <MenuItem key={member.id} value={member.id}>
                          {member.name} ({member.email})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  onClick={handleAddMember}
                  fullWidth
                  disabled={!selectedMember || formData.addAllTeamMembers}
                >
                  Add Member
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box mt={3}>
            {!formData.addAllTeamMembers && (
              <>
                {formData.sendEmailTo.length > 0 ? (
                  <List disablePadding>
                    {formData.sendEmailTo.map((member) => (
                      <MemberItem key={member.id}>
                        <Box>
                          <Typography variant="subtitle2">
                            {member.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {member.email}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveMember(member.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </MemberItem>
                    ))}
                  </List>
                ) : (
                  <Box
                    py={2}
                    px={3}
                    bgcolor="action.hover"
                    borderRadius={1}
                    textAlign="center"
                  >
                    <Typography variant="body2" color="textSecondary">
                      No team members selected to receive notifications
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {formData.addAllTeamMembers && (
              <Box
                py={2}
                px={3}
                bgcolor="action.hover"
                borderRadius={1}
                textAlign="center"
              >
                <Typography variant="body2">
                  All team members will be notified when this form is submitted
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="flex-start" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          size="large"
          disabled={updateLoading}
        >
          {updateLoading ? "Saving..." : "Save Changes"}
        </Button>
        {updateLoading && <CircularProgress size={24} sx={{ ml: 2 }} />}
      </Box>

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FormTab;
